import { useEffect, useState } from 'react'
import { adminRequest } from './EshopCategories'

export type FiskalGroup = { key: string; number: number | null; fromFiskal: boolean; name: string; apiName: string; customName: string; inactiveInRegister: boolean; productCount: number; manualCount: number }
export type FiskalGroupsInfo = { namesLoadedAt: string | null; canLoadNames: boolean; loadError?: string }

/** Popis skupiny pre zobrazenie: vlastný názov, inak technický názov z pokladne. */
export function fiskalGroupLabel(groups: FiskalGroup[], key: string) {
  const group = groups.find(g => g.key === key)
  if (group && !group.fromFiskal) return `${key} (mimo pokladne)`
  return group?.name ? group.name : key
}

export default function FiskalGroupsDialog({ groups, info, onClose, onChanged }: {
  groups: FiskalGroup[]
  info: FiskalGroupsInfo
  onClose: () => void
  onChanged: (groups: FiskalGroup[], productsMoved: boolean, info?: FiskalGroupsInfo) => void
}) {
  const [names, setNames] = useState<Record<string, string>>({})
  const [moveTo, setMoveTo] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => { setNames(Object.fromEntries(groups.filter(g => g.fromFiskal).map(g => [g.key, g.customName]))) }, [groups])

  const fiskal = groups.filter(g => g.fromFiskal)
  const other = groups.filter(g => !g.fromFiskal)
  const changed = fiskal.some(g => (names[g.key] ?? '').trim() !== g.customName)
  const [customOpen, setCustomOpen] = useState(false)

  async function saveNames() {
    setBusy(true); setError(''); setMessage('')
    try {
      const data = await adminRequest('/api/admin/fiskal-groups/names', { method: 'PUT', body: JSON.stringify({ names }) })
      onChanged(data.groups, false)
      setMessage('Názvy skupín sú uložené.')
    } catch (e) { setError(e instanceof Error ? e.message : 'Uloženie zlyhalo.') } finally { setBusy(false) }
  }

  async function refresh() {
    setBusy(true); setError(''); setMessage('')
    try {
      const data = await adminRequest('/api/admin/fiskal-groups/refresh', { method: 'POST' })
      onChanged(data.groups, false, { namesLoadedAt: data.namesLoadedAt, canLoadNames: data.canLoadNames })
      setMessage('Názvy skupín sú načítané z pokladne.')
    } catch (e) { setError(e instanceof Error ? e.message : 'Načítanie zlyhalo.') } finally { setBusy(false) }
  }

  async function move(from: string) {
    const to = moveTo[from]
    if (!to) return
    const target = fiskalGroupLabel(groups, to)
    if (!window.confirm(`Presunúť všetky produkty zo skupiny „${from}“ do skupiny „${target}“? Skupina „${from}“ potom zmizne.`)) return
    setBusy(true); setError(''); setMessage('')
    try {
      const data = await adminRequest('/api/admin/fiskal-groups/move', { method: 'POST', body: JSON.stringify({ from, to }) })
      onChanged(data.groups, true)
      setMessage(`Presunuté produkty: ${data.moved}. Skupina „${from}“ je zrušená.`)
    } catch (e) { setError(e instanceof Error ? e.message : 'Presun zlyhal.') } finally { setBusy(false) }
  }

  return <div className="modal-backdrop" onClick={onClose}>
    <div className="product-form fiskal-groups-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-label="Skupiny z pokladne">
      <div className="panel-heading"><div><h2>Skupiny z pokladne</h2><p>Názvy sa načítavajú z pokladne FiskalPRO. Zmeniť ich môžete v pokladni – sem sa prenesú pri ďalšom načítaní.</p></div>
        <button type="button" className="close-btn" onClick={onClose}>✕</button></div>

      <div className="fiskal-group-sync">
        <span>{info.namesLoadedAt ? `Naposledy načítané: ${new Date(info.namesLoadedAt).toLocaleString('sk-SK', { timeZone: 'Europe/Bratislava' })}` : 'Názvy ešte neboli načítané.'}</span>
        {info.canLoadNames && <button type="button" className="text-btn" disabled={busy} onClick={() => void refresh()}>↻ Načítať názvy z pokladne</button>}
      </div>
      {info.loadError && <p className="error-message">{info.loadError}</p>}

      <div className="fiskal-group-list">
        {fiskal.map(group => <div className={`fiskal-group-row ${customOpen ? 'has-custom' : ''}`} key={group.key}>
          <span className="fiskal-group-number">{group.number ?? '–'}</span>
          <div className="fiskal-group-name">
            <strong>{group.apiName || group.key}</strong>
            {group.inactiveInRegister && <small className="form-warning">v pokladni neaktívna</small>}
            {!customOpen && group.customName && <small>v administrácii: {group.customName}</small>}
          </div>
          {customOpen && <input aria-label={`Vlastný názov pre ${group.key}`} maxLength={60} placeholder="vlastný názov (nepovinné)" value={names[group.key] ?? ''} onChange={e => setNames({ ...names, [group.key]: e.target.value })} />}
          <small>{group.productCount} {group.productCount === 1 ? 'produkt' : group.productCount < 5 ? 'produkty' : 'produktov'}</small>
        </div>)}
        {!fiskal.length && <p className="hint">Zatiaľ nie sú načítané žiadne produkty z pokladne FiskalPRO.</p>}
      </div>
      {!customOpen && <button type="button" className="text-btn fiskal-custom-toggle" onClick={() => setCustomOpen(true)}>Chcem niektorej skupine dať iný názov len v administrácii</button>}

      {other.length > 0 && <div className="fiskal-group-other">
        <h3>Skupiny, ktoré nie sú v pokladni</h3>
        <p className="hint">Vznikli pri ručne pridaných produktoch (formulár mal vopred vyplnené „Torty“). Produkty presuňte do skupiny z pokladne a táto skupina zmizne.</p>
        {other.map(group => <div className="fiskal-group-row is-other" key={group.key}>
          <strong>{group.key}</strong>
          <small>{group.productCount} {group.productCount === 1 ? 'produkt' : group.productCount < 5 ? 'produkty' : 'produktov'}</small>
          <select aria-label={`Presunúť produkty zo skupiny ${group.key}`} value={moveTo[group.key] ?? ''} onChange={e => setMoveTo({ ...moveTo, [group.key]: e.target.value })}>
            <option value="">Presunúť do…</option>
            {fiskal.map(target => <option key={target.key} value={target.key}>{target.name ? `${target.name} (skupina ${target.number})` : target.key}</option>)}
          </select>
          <button type="button" className="cancel-btn" disabled={busy || !moveTo[group.key]} onClick={() => void move(group.key)}>Presunúť a zrušiť</button>
        </div>)}
      </div>}

      {error && <p className="error-message">{error}</p>}
      {message && <p className="success-message">{message}</p>}
      <div className="form-actions">
        <button type="button" className="cancel-btn" onClick={onClose}>Zavrieť</button>
        {customOpen && <button type="button" className="primary-btn" disabled={busy || !changed} onClick={() => void saveNames()}>{busy ? 'Ukladám…' : 'Uložiť vlastné názvy'}</button>}
      </div>
    </div>
  </div>
}
